"use client";

import { imageApiUrl } from "@/lib/images/urls";

import Image from "next/image";
import Link from "next/link";
import { useMessages } from "next-intl";
import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { Flame, Layers, Mic2, Sparkles, Ticket } from "lucide-react";

import type { SiteCopy, SiteLanguage } from "@/lib/site-data";
import { withLanguagePrefix } from "@/lib/site-locale";
import { formatDate, formatDateTime } from "@/lib/site-date";
import type { DashboardData, StoryRecord } from "@/lib/site-queries";
import type { ClusterRecord } from "@/lib/data/shared/types";
import { eventHasImage } from "@/lib/data/shared/event-has-image";
import type {
  ArtistMomentumRow,
  GenreMomentumRow,
  HomeShelfCluster,
  HomeShelves,
} from "@/lib/data/home/shelves";
import { BandBadge } from "@/components/site/band-badge";
import { HomeFooter } from "@/components/site/home-footer";
import { HorizontalShelf } from "@/components/site/horizontal-shelf";
import { usePreferences } from "@/components/site/preferences-provider";
import { ThatRadioThingClient } from "@/components/site/thatradiothing-client";
import { Button } from "@/components/ui/button";
import { filterEventsByLocation } from "@/lib/site-preferences";
import { useThatradiothingOnline } from "@/lib/use-thatradiothing-online";
import { cn } from "@/lib/utils";

// `useSyncExternalStore` needs a subscribe fn; we have nothing to
// subscribe to. We only use it to flip a boolean after hydration so
// preferences-dependent filters don't cause SSR/CSR mismatch.
const subscribeNoop = () => () => {};

function pickTranslation(
  translations: Array<{ language: string; title: string; summary: string }>,
  language: SiteLanguage,
) {
  return translations.find((item) => item.language === language) ?? translations[0] ?? null;
}

// Human-label the origin market for a cluster. Each locale ships its
// own translation for the four markets we currently ingest from.
function originLabel(originalLanguage: string | null, copy: SiteCopy): string {
  if (originalLanguage === "tr") return copy.ui.countryTurkey;
  if (originalLanguage === "de") return copy.ui.countryGermany;
  if (originalLanguage === "uk") return copy.ui.countryUkraine;
  return copy.ui.countryGlobal;
}

// Event primary-link fallback — when an event has no partner URL we
// degrade to the full /events page (filtered to user's location).
function eventHref(event: { url: string | null }, language: SiteLanguage): string {
  if (event.url) return event.url;
  return withLanguagePrefix("/events", language);
}

function ticketHref(event: { url: string | null }): string | null {
  return event.url;
}

function countryLabel(countryCode: string | null, copy: SiteCopy): string {
  const normalized = countryCode?.trim().toLowerCase();
  if (normalized === "tr") return copy.ui.countryTurkey;
  if (normalized === "us") return copy.ui.countryUnitedStates;
  if (normalized === "de") return copy.ui.countryGermany;
  if (normalized === "ua") return copy.ui.countryUkraine;
  if (normalized === "uk" || normalized === "gb") return copy.ui.countryUnitedKingdom;
  if (!normalized) return copy.ui.countryGlobal;
  return normalized.toUpperCase();
}

/**
 * HomeView — the editorial wall.
 *
 * Layout is deliberately lean compared to the previous dashboard-y
 * version. Intercept-inspired full-bleed lead hero sits at the top;
 * underneath, a single horizontal strip of outlet pills routes to the
 * original articles in new tabs (the routing is the point of this
 * product). Below the hero: Top Stories grid and Tour Signals strip.
 * The prior "Coverage Map" text list and the stats-block footer are
 * gone — they were ops/analytics, not journalism.
 *
 * Radio dock behavior is preserved exactly: big inline card on the
 * right at `lg+` when thatradiothing is online; collapses to a single
 * column otherwise so the hero reclaims the space.
 */
export function HomeView({
  data,
  shelves,
  language,
}: {
  data: DashboardData;
  shelves: HomeShelves;
  language: SiteLanguage;
}) {
  // `language` is authoritative; preferences-backed language is only
  // used indirectly (other preferences like selected sources / location
  // still live there). See clusters-view.tsx for the deeper rationale.
  const { selectedSourceNames, sourceOptions, eventLocation, eventRadiusKm } = usePreferences();
  const copy = useMessages() as SiteCopy;
  const [failedClusterImages, setFailedClusterImages] = useState<Set<string>>(new Set());

  // Radio online probe — when thatradiothing is unreachable we hide the
  // inline card and let the hero fill the full row.
  const radioOnline = useThatradiothingOnline();
  const radioSlotVisible = radioOnline !== false;

  // Hydration-safe gate: SSR + first client render both see `false`,
  // so preference-dependent filters don't mismatch. The second client
  // render flips to `true` and the real filters apply.
  const preferencesReady = useSyncExternalStore(
    subscribeNoop,
    () => true,
    () => false,
  );

  // Home's top-stories list MUST be filtered on the server. The old
  // approach was "fetch the latest N globally, then drop rows whose
  // source isn't in the user's picks" — that shows 0–2 rows when the
  // picks are narrow, because the filter ran after the top-N slice.
  // Per the AGENTS.md source-filter convention, we hit
  // `/api/news/feed?mode=top&sources=…` — the filter lives inside the
  // Prisma query and returns a proper top-N *of the selected sources*.
  //
  // First paint uses the server-rendered `data.topStories` so the
  // page is never blank; the filtered list swaps in once prefs hydrate
  // and the fetch settles. We tag the cached result with the sources
  // key so a stale payload from the prior selection can't flash.
  const sourcesKey = useMemo(() => selectedSourceNames.slice().sort().join("||"), [selectedSourceNames]);
  const [filtered, setFiltered] = useState<{ sourcesKey: string; items: StoryRecord[] } | null>(null);

  // `data.topStories` comes from the server with no source filter
  // applied — effectively "all sources". If the user's selection
  // covers every known source we'd re-fetch and swap the lead for no
  // reason (just because /api/news/feed's ranking differs slightly
  // from getDashboardData's), which reads as a flicker. Only fire
  // the fetch when the user has actually narrowed the picks.
  const allSourceNames = useMemo(
    () => new Set(sourceOptions.map((option) => option.name)),
    [sourceOptions],
  );
  const hasNarrowedSources = useMemo(() => {
    if (!preferencesReady) return false;
    if (allSourceNames.size === 0) return false; // sources still bootstrapping
    if (selectedSourceNames.length === 0) return false;
    if (selectedSourceNames.length >= allSourceNames.size) return false;
    return selectedSourceNames.some((name) => !allSourceNames.has(name))
      ? true
      : selectedSourceNames.length < allSourceNames.size;
  }, [preferencesReady, allSourceNames, selectedSourceNames]);

  useEffect(() => {
    if (!hasNarrowedSources) {
      return;
    }

    // Abort-guard: rapid source toggles can stack requests — the
    // latest one wins.
    const controller = new AbortController();
    const params = new URLSearchParams({
      mode: "top",
      limit: "12",
      sources: selectedSourceNames.join(","),
    });
    void fetch(`/api/news/feed?${params.toString()}`, {
      cache: "no-store",
      signal: controller.signal,
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((payload) => {
        if (!payload) return;
        setFiltered({ sourcesKey, items: payload.items as StoryRecord[] });
      })
      .catch(() => {
        // Swallow — on failure we keep the server-rendered baseline
        // rather than flashing an empty state.
      });

    return () => controller.abort();
  }, [hasNarrowedSources, sourcesKey, selectedSourceNames]);

  // Only honor the cached filtered list when (a) the user has
  // actively narrowed their sources and (b) the cached payload was
  // fetched with the exact same selection. If they haven't narrowed,
  // or the cache is stale, fall back to the server-rendered baseline
  // so the lead doesn't flicker on first load.
  const regionalTopStories =
    hasNarrowedSources && filtered?.sourcesKey === sourcesKey
      ? filtered.items
      : data.topStories;

  const regionalSignals = useMemo(() => {
    if (!preferencesReady) return data.events;
    return filterEventsByLocation(data.events, eventLocation, eventRadiusKm);
  }, [data.events, eventLocation, eventRadiusKm, preferencesReady]);
  const visibleEvents = useMemo(() => regionalSignals.slice(0, 4), [regionalSignals]);

  const lead = regionalTopStories[0];
  const leadTranslation = lead ? pickTranslation(lead.translations, language) : null;
  const leadShowImage = Boolean(lead?.hasImage && !failedClusterImages.has(lead?.id ?? ""));

  // Non-lead top stories live in a 3-card grid under the hero.
  const topStoriesRest = regionalTopStories.slice(1, 7);

  const markClusterImageFailed = (clusterId: string) => {
    setFailedClusterImages((current) => {
      if (current.has(clusterId)) return current;
      const next = new Set(current);
      next.add(clusterId);
      return next;
    });
  };

  return (
    <div className="space-y-12">
      {/* ----------------------------------------------------------------
          LEAD HERO + RADIO
          The hero is an Intercept-style full-bleed image card. Title,
          LLM summary, and outlet pills sit over a dark gradient so
          they stay legible on busy photos.
      ---------------------------------------------------------------- */}
      <section
        className={cn(
          "grid gap-6",
          radioSlotVisible ? "lg:grid-cols-[minmax(0,1.4fr)_minmax(0,0.6fr)]" : "lg:grid-cols-1",
        )}
      >
        <article className="group relative min-h-[420px] overflow-hidden rounded-3xl border border-border bg-card/60 sm:min-h-[520px]">
          {/* Background media — the real hero element. */}
          <div className="absolute inset-0">
            {leadShowImage && lead ? (
              <Image
                src={imageApiUrl("cluster", lead.id)}
                alt={leadTranslation?.title ?? "Lead story"}
                fill
                priority
                sizes="(max-width: 1024px) 100vw, 1100px"
                className="object-cover transition-transform duration-[1200ms] group-hover:scale-[1.02]"
                onError={() => lead && markClusterImageFailed(lead.id)}

              />
            ) : (
              <div className="aurora-fallback absolute inset-0" aria-hidden="true" />
            )}
            {/* Darken bottom half for legibility; leaving the top clean
                so the photography still breathes. */}
            <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/95 via-zinc-950/55 to-zinc-950/10" />
          </div>

          <div className="relative z-10 flex h-full min-h-[inherit] flex-col justify-end gap-4 p-6 sm:gap-5 sm:p-10">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-rose-500/90 px-2.5 py-0.5 text-[10px] font-mono font-semibold uppercase tracking-[0.18em] text-white">
                {copy.ui.leadStory}
              </span>
              {lead ? (
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/70">
                  {formatDateTime(lead.updatedAt, language)}
                </span>
              ) : null}
              {lead ? (
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/70">
                  · {originLabel(lead.originalLanguage, copy)}
                </span>
              ) : null}
            </div>

            <h1 className="max-w-4xl text-3xl font-black leading-[1.05] tracking-tight text-white sm:text-5xl md:text-6xl">
              {leadTranslation?.title ?? copy.heroTitle}
            </h1>

            <p className="max-w-3xl text-sm leading-relaxed text-white/80 sm:text-base">
              {leadTranslation?.summary ?? lead?.ogDescription ?? lead?.shortContent ?? copy.heroBody}
            </p>

            {/* Outlet pill row — THE primary action on this hero.
                Every pill opens the source article in a new tab so the
                user keeps this duudey tab (and their Spotify playback)
                alive in the background. Label format is just the
                outlet name; the article title appears on the secondary
                link list further down. */}
            {lead && lead.articles.length > 0 ? (
              <div className="flex flex-wrap items-center gap-1.5 pt-1">
                {lead.articles.slice(0, 5).map((article) => (
                  <a
                    key={article.id}
                    href={article.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center rounded-full border border-white/25 bg-white/10 px-3 py-1 text-[11px] font-mono uppercase tracking-[0.12em] text-white backdrop-blur-md transition hover:border-rose-300/60 hover:bg-rose-500/25"
                  >
                    {article.sourceName}
                  </a>
                ))}
              </div>
            ) : null}

            <div className="flex flex-wrap gap-2 pt-2">
              {/* seeCoverage is the internal expansion that shows all
                  outlets + translations on one page. Secondary to the
                  outlet pills above, because that's where we actually
                  drive partner traffic. */}
              <Button
                asChild
                size="lg"
                className="rounded-full bg-rose-500 text-white hover:bg-rose-400"
              >
                <Link
                  href={
                    lead
                      ? withLanguagePrefix(`/clusters/${lead.id}`, language)
                      : withLanguagePrefix("/news", language)
                  }
                >
                  {copy.ui.seeCoverage}
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="rounded-full border-white/30 bg-white/10 text-white backdrop-blur-md hover:bg-white/20"
              >
                <Link href={withLanguagePrefix("/clusters", language)}>{copy.ui.viewAllClusters}</Link>
              </Button>
            </div>
          </div>
        </article>

        {/* Radio card — no behavior change; the inline `home` mode is
            the user's prior design decision. Lives here on `lg+`, stacks
            below the hero on narrower screens. */}
        {radioSlotVisible ? (
          <ThatRadioThingClient
            mode="home"
            title={copy.ui.radioClientTitle}
            subtitle={copy.ui.radioClientSubtitle}
            openLabel={copy.ui.openRadioClient}
          />
        ) : null}
      </section>

      {/* ----------------------------------------------------------------
          TOP STORIES — 3-card grid using the same image-forward
          language as the lead. No per-card "Coverage Metadata" box;
          that used to read as telemetry and didn't help readers choose.
      ---------------------------------------------------------------- */}
      {topStoriesRest.length > 0 ? (
        <section className="space-y-4">
          <header className="flex items-baseline justify-between gap-4">
            <h2 className="text-2xl font-black tracking-tight text-foreground sm:text-3xl">{copy.ui.topStories}</h2>
            <Link
              href={withLanguagePrefix("/clusters", language)}
              className="text-xs font-mono uppercase tracking-[0.16em] text-rose-600 hover:text-rose-500 dark:text-rose-300 dark:hover:text-rose-200"
            >
              {copy.ui.viewAllClusters} →
            </Link>
          </header>
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {topStoriesRest.map((item) => {
              const storyTranslation = pickTranslation(item.translations, language);
              const showImage = item.hasImage && !failedClusterImages.has(item.id);

              return (
                <article
                  key={item.id}
                  className="flex flex-col overflow-hidden rounded-2xl border border-border bg-card/50 transition hover:border-rose-400/40"
                >
                  <Link
                    href={withLanguagePrefix(`/clusters/${item.id}`, language)}
                    className="relative block aspect-[16/9] w-full overflow-hidden bg-muted"
                  >
                    {showImage ? (
                      <Image
                        src={imageApiUrl("cluster", item.id)}
                        alt={storyTranslation?.title ?? "Story image"}
                        fill
                        sizes="(max-width: 1024px) 100vw, 420px"
                        className="object-cover transition-transform duration-500 hover:scale-[1.03]"
                        onError={() => markClusterImageFailed(item.id)}

                      />
                    ) : (
                      <div className="aurora-fallback absolute inset-0" aria-hidden="true" />
                    )}
                  </Link>
                  <div className="flex flex-1 flex-col gap-3 p-4">
                    <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.16em] text-muted-foreground">
                      <span>{formatDate(item.updatedAt, language)}</span>
                      <span>·</span>
                      <span>{originLabel(item.originalLanguage, copy)}</span>
                    </div>
                    <Link
                      href={withLanguagePrefix(`/clusters/${item.id}`, language)}
                      className="text-base font-bold leading-snug text-foreground hover:text-rose-600 dark:hover:text-rose-300"
                    >
                      {storyTranslation?.title ?? copy.ui.untitledStory}
                    </Link>
                    <p className="text-xs leading-relaxed text-muted-foreground line-clamp-3">
                      {storyTranslation?.summary ?? item.ogDescription ?? item.shortContent ?? copy.ui.noSummary}
                    </p>
                    {/* Outlet links — outbound, one row, truncated to 2
                        with a "+N" trailing indicator. */}
                    <div className="flex flex-wrap gap-1.5">
                      {item.articles.slice(0, 2).map((article) => (
                        <a
                          key={article.id}
                          href={article.url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center rounded-full border border-border bg-muted px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-[0.12em] text-foreground transition hover:border-rose-300/60 hover:bg-rose-400/10"
                        >
                          {article.sourceName}
                        </a>
                      ))}
                      {item.articles.length > 2 ? (
                        <span className="inline-flex items-center rounded-full border border-border px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-[0.12em] text-muted-foreground">
                          +{item.articles.length - 2}
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-auto flex flex-wrap items-center gap-1.5 pt-1">
                      {item.bands.slice(0, 3).map((band) => (
                        <BandBadge key={band.name} band={band} language={language} />
                      ))}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      ) : null}

      {/* ----------------------------------------------------------------
          TOUR SIGNALS — horizontal row of event cards, image-led.
          Kept close to the old "Tour Signals" column but now runs
          full-width so images are bigger and the cards breathe.
      ---------------------------------------------------------------- */}
      <section className="space-y-4">
        <header className="flex items-baseline justify-between gap-4">
          <h2 className="text-2xl font-black tracking-tight text-foreground sm:text-3xl">{copy.sections.toursAndEvents}</h2>
          {regionalSignals.length > visibleEvents.length ? (
            <Link
              href={withLanguagePrefix("/events", language)}
              className="text-xs font-mono uppercase tracking-[0.16em] text-rose-600 hover:text-rose-500 dark:text-rose-300 dark:hover:text-rose-200"
            >
              {copy.ui.viewMoreEvents} →
            </Link>
          ) : null}
        </header>

        {visibleEvents.length === 0 ? (
          <p className="text-sm text-muted-foreground">{copy.ui.noEventsForLocation}</p>
        ) : (
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {visibleEvents.map((event) => {
              const ticketsUrl = ticketHref(event);
              // The ingestion pipeline tags nearly every event as "music";
              // show the category only when it adds signal (festival,
              // conference, etc.).
              const hasMeaningfulCategory = Boolean(event.category && event.category.toLowerCase() !== "music");
              return (
                <article
                  key={event.id}
                  className="flex flex-col overflow-hidden rounded-2xl border border-border bg-card/50 transition hover:border-rose-400/40"
                >
                  <div className="relative aspect-[16/10] w-full overflow-hidden bg-muted">
                    {eventHasImage(event) ? (
                      // Routed through our same-origin proxy so the
                      // upstream poster (the ticketing CDN) doesn't
                      // get to set first-party cookies that browsers
                      // would treat as third-party tracking. See
                      // `src/app/api/images/event/[id]/route.ts` for
                      // the rationale.
                      <Image
                        src={imageApiUrl("event", event.id)}
                        alt={event.title}
                        fill
                        sizes="(max-width: 1024px) 100vw, 320px"
                        className="object-cover transition-transform duration-500 hover:scale-[1.03]"
                      />
                    ) : (
                      <div className="aurora-fallback absolute inset-0" aria-hidden="true" />
                    )}
                  </div>
                  <div className="flex flex-1 flex-col gap-2 p-4">
                    <div className="flex items-center justify-between gap-2 text-[10px] font-mono uppercase tracking-[0.16em] text-muted-foreground">
                      <span>{formatDate(event.startAt, language)}</span>
                      {hasMeaningfulCategory ? (
                        <span className="rounded-full border border-rose-300/40 bg-rose-400/10 px-2 py-0.5 text-rose-700 dark:text-rose-200">
                          {event.category}
                        </span>
                      ) : null}
                    </div>
                    {event.url ? (
                      <a
                        href={eventHref(event, language)}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm font-bold leading-snug text-foreground hover:text-rose-600 dark:hover:text-rose-300"
                      >
                        {event.title}
                      </a>
                    ) : (
                      <Link
                        href={eventHref(event, language)}
                        className="text-sm font-bold leading-snug text-foreground hover:text-rose-600 dark:hover:text-rose-300"
                      >
                        {event.title}
                      </Link>
                    )}
                    <p className="text-xs text-muted-foreground line-clamp-2">{event.description ?? copy.ui.noSummary}</p>
                    <p className="mt-auto text-[10px] font-mono uppercase tracking-[0.16em] text-muted-foreground">
                      {(event.city ?? copy.ui.unknownCity) + " · " + countryLabel(event.countryCode, copy)}
                    </p>
                    {ticketsUrl ? (
                      <Button
                        asChild
                        size="sm"
                        variant="outline"
                        className="mt-1 w-full rounded-full border-border bg-muted text-foreground hover:border-rose-300/60 hover:bg-rose-400/10"
                      >
                        <a href={ticketsUrl} target="_blank" rel="noreferrer">
                          <Ticket className="mr-1.5 size-3.5" aria-hidden="true" />
                          {event.provider.name}
                        </a>
                      </Button>
                    ) : null}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      {/* ----------------------------------------------------------------
          HEATING UP — clusters with the most articles published in the
          last 48h. This is the "news is landing fastest right now" lens.
          Renders as a horizontally scrolling strip of big image cards.
      ---------------------------------------------------------------- */}
      {shelves.heatingUp.length > 0 ? (
        <ShelfHeader
          title={copy.ui.heatingUp}
          subtitle={copy.ui.heatingUpIntro}
          icon={<Flame className="size-4 text-rose-500" aria-hidden="true" />}
        >
          <HorizontalShelf>
            {shelves.heatingUp.map((cluster) => (
              <HeatingUpCard
                key={cluster.id}
                cluster={cluster}
                copy={copy}
                language={language}
              />
            ))}
          </HorizontalShelf>
        </ShelfHeader>
      ) : null}

      {/* ----------------------------------------------------------------
          DEEP COVERAGE — clusters with the most distinct outlets on
          them. Outlets are the star of these cards (big pill stack).
      ---------------------------------------------------------------- */}
      {shelves.deepCoverage.length > 0 ? (
        <ShelfHeader
          title={copy.ui.deepCoverage}
          subtitle={copy.ui.deepCoverageIntro}
          icon={<Layers className="size-4 text-rose-500" aria-hidden="true" />}
        >
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {shelves.deepCoverage.map((cluster) => (
              <DeepCoverageCard
                key={cluster.id}
                cluster={cluster}
                copy={copy}
                language={language}
              />
            ))}
          </div>
        </ShelfHeader>
      ) : null}

      {/* ----------------------------------------------------------------
          SCENES NOW — top 3 genres this week, each with their top N
          fresh stories. Uses a "genre rail" per row.
      ---------------------------------------------------------------- */}
      {shelves.genreMomentum.length > 0 ? (
        <ShelfHeader title={copy.ui.scenesNow} subtitle={copy.ui.scenesNowIntro}>
          <div className="space-y-8">
            {shelves.genreMomentum.map((row) => (
              <GenreRail key={row.id} row={row} copy={copy} language={language} />
            ))}
          </div>
        </ShelfHeader>
      ) : null}

      {/* ----------------------------------------------------------------
          ARTISTS ON THE RISE — top 6 bands by recent article spike.
          Each card pairs the band avatar with their freshest story's
          hero image so the row is maximally scannable.
      ---------------------------------------------------------------- */}
      {shelves.artistMomentum.length > 0 ? (
        <ShelfHeader
          title={copy.ui.artistsOnTheRise}
          subtitle={copy.ui.artistsOnTheRiseIntro}
          icon={<Sparkles className="size-4 text-rose-500" aria-hidden="true" />}
        >
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {shelves.artistMomentum.map((row) => (
              <ArtistMomentumCard
                key={row.id}
                row={row}
                copy={copy}
                language={language}
              />
            ))}
          </div>
        </ShelfHeader>
      ) : null}

      <HomeFooter language={language} copy={copy} />
    </div>
  );
}

// ---------------------------------------------------------------------
// Shelf primitives — the four algorithmic shelves below the hero all
// share this header + children layout. Keeping them inline (not in a
// separate file) because they're only used by HomeView.
// ---------------------------------------------------------------------

function ShelfHeader({
  title,
  subtitle,
  icon,
  children,
}: {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-4">
      <header className="space-y-1">
        <div className="flex items-center gap-2">
          {icon}
          <h2 className="text-2xl font-black tracking-tight text-foreground sm:text-3xl">{title}</h2>
        </div>
        {subtitle ? <p className="text-xs text-muted-foreground sm:text-sm">{subtitle}</p> : null}
      </header>
      {children}
    </section>
  );
}

function HeatingUpCard({
  cluster,
  copy,
  language,
}: {
  cluster: HomeShelfCluster;
  copy: SiteCopy;
  language: SiteLanguage;
}) {
  const translation = cluster.translations.find((t) => t.language === language) ?? cluster.translations[0];
  return (
    <Link
      href={withLanguagePrefix(`/clusters/${cluster.id}`, language)}
      className="group relative flex min-w-[260px] max-w-[320px] shrink-0 snap-start flex-col gap-3 overflow-hidden rounded-2xl border border-border bg-card/50 transition hover:border-rose-400/50 sm:min-w-[300px]"
    >
      <div className="relative aspect-[16/10] w-full overflow-hidden bg-muted">
        {cluster.hasImage ? (
          <Image
            src={imageApiUrl("cluster", cluster.id)}
            alt={translation?.title ?? "Story"}
            fill
            sizes="300px"
            className="object-cover transition-transform duration-500 group-hover:scale-[1.04]"

          />
        ) : (
          <div className="aurora-fallback absolute inset-0" aria-hidden="true" />
        )}
        {/* Rose "new in Nh" pill is the heart of this shelf — it
            tells the reader *why* this card is here. Positioned over
            the image so it reads as a live badge, not metadata. */}
        {cluster.recentArticleCount && cluster.recentArticleCount > 0 ? (
          <span className="absolute left-3 top-3 flex items-center gap-1 rounded-full bg-rose-500/95 px-2 py-0.5 text-[10px] font-mono font-semibold uppercase tracking-[0.14em] text-white">
            <Flame className="size-3" aria-hidden="true" />
            {copy.ui.newArticlesIn.replace("{hours}", "48")} · {cluster.recentArticleCount}
          </span>
        ) : null}
      </div>
      <div className="flex flex-col gap-2 px-4 pb-4">
        <p className="text-sm font-bold leading-snug text-foreground group-hover:text-rose-600 dark:group-hover:text-rose-300">
          {translation?.title ?? copy.ui.untitledStory}
        </p>
        <p className="text-[11px] text-muted-foreground line-clamp-2">
          {translation?.summary ?? copy.ui.noSummaryYet}
        </p>
      </div>
    </Link>
  );
}

function DeepCoverageCard({
  cluster,
  copy,
  language,
}: {
  cluster: ClusterRecord;
  copy: SiteCopy;
  language: SiteLanguage;
}) {
  const translation = cluster.translations.find((t) => t.language === language) ?? cluster.translations[0];
  const pills = cluster.articleUrls.slice(0, 4).map((url, index) => ({
    url,
    outlet: cluster.sourceNames[index] ?? "source",
  }));
  const extra = cluster.sourceNames.length - pills.length;
  return (
    <article className="flex flex-col gap-3 rounded-2xl border border-border bg-card/50 p-4 transition hover:border-rose-400/40">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="inline-flex items-center gap-1 rounded-full bg-rose-500/90 px-2.5 py-0.5 text-[10px] font-mono font-semibold uppercase tracking-[0.14em] text-white">
          <Layers className="size-3" aria-hidden="true" />
          {copy.ui.outletsOnIt.replace("{count}", String(cluster.sourceNames.length))}
        </span>
      </div>
      <Link
        href={withLanguagePrefix(`/clusters/${cluster.id}`, language)}
        className="text-base font-bold leading-snug text-foreground hover:text-rose-600 dark:hover:text-rose-300"
      >
        {translation?.title ?? copy.ui.untitledStory}
      </Link>
      <p className="text-xs text-muted-foreground line-clamp-2">
        {translation?.summary ?? copy.ui.noSummaryYet}
      </p>
      {/* Outlet pills are prominent here because that's the whole
          point of this shelf — showcasing the breadth of coverage. */}
      <div className="flex flex-wrap gap-1.5">
        {pills.map((pill) => (
          <a
            key={pill.url}
            href={pill.url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center rounded-full border border-border bg-muted px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-[0.12em] text-foreground transition hover:border-rose-300/60 hover:bg-rose-400/10"
          >
            {pill.outlet}
          </a>
        ))}
        {extra > 0 ? (
          <span className="inline-flex items-center rounded-full border border-border px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-[0.12em] text-muted-foreground">
            +{extra}
          </span>
        ) : null}
      </div>
      <div className="flex flex-wrap gap-1.5 pt-1">
        {cluster.bands.slice(0, 3).map((band) => (
          <BandBadge key={band.name} band={band} language={language} />
        ))}
      </div>
    </article>
  );
}

function GenreRail({
  row,
  copy,
  language,
}: {
  row: GenreMomentumRow;
  copy: SiteCopy;
  language: SiteLanguage;
}) {
  return (
    <div className="space-y-3">
      <header className="flex items-baseline justify-between gap-4">
        <div className="flex items-baseline gap-3">
          <h3 className="text-xl font-black capitalize tracking-tight text-foreground">
            {row.name}
          </h3>
          <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-muted-foreground">
            {row.recentClusterCount} {copy.ui.storiesCount.replace("{count}", "").trim()}
          </span>
        </div>
        <Link
          href={withLanguagePrefix(`/genres/${row.slug}`, language)}
          className="text-[11px] font-mono uppercase tracking-[0.16em] text-rose-600 hover:text-rose-500 dark:text-rose-300"
        >
          {copy.ui.seeGenre} →
        </Link>
      </header>
      <div className="grid gap-4 md:grid-cols-3">
        {row.clusters.map((cluster) => {
          const translation = cluster.translations.find((t) => t.language === language) ?? cluster.translations[0];
          return (
            <Link
              key={cluster.id}
              href={withLanguagePrefix(`/clusters/${cluster.id}`, language)}
              className="group flex flex-col overflow-hidden rounded-xl border border-border bg-card/50 transition hover:border-rose-400/40"
            >
              <div className="relative aspect-[16/10] w-full overflow-hidden bg-muted">
                {cluster.hasImage ? (
                  <Image
                    src={imageApiUrl("cluster", cluster.id)}
                    alt={translation?.title ?? "Story"}
                    fill
                    sizes="320px"
                    className="object-cover transition-transform duration-500 group-hover:scale-[1.04]"

                  />
                ) : (
                  <div className="aurora-fallback absolute inset-0" aria-hidden="true" />
                )}
              </div>
              <div className="flex flex-col gap-1.5 p-3">
                <p className="text-sm font-bold leading-snug text-foreground line-clamp-2 group-hover:text-rose-600 dark:group-hover:text-rose-300">
                  {translation?.title ?? copy.ui.untitledStory}
                </p>
                <p className="text-[10px] font-mono uppercase tracking-[0.14em] text-muted-foreground">
                  {cluster.sourceNames.slice(0, 2).join(" · ")}
                </p>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function ArtistMomentumCard({
  row,
  copy,
  language,
}: {
  row: ArtistMomentumRow;
  copy: SiteCopy;
  language: SiteLanguage;
}) {
  const translation = row.featuredCluster
    ? row.featuredCluster.translations.find((t) => t.language === language) ?? row.featuredCluster.translations[0]
    : null;
  return (
    <Link
      href={withLanguagePrefix(`/artists/${row.slug}`, language)}
      className="group relative flex flex-col gap-3 overflow-hidden rounded-2xl border border-border bg-card/50 transition hover:border-rose-400/50"
    >
      {/* Hero row — featured cluster image fills the card top; band
          avatar floats over the top-left corner. */}
      <div className="relative aspect-[16/9] w-full overflow-hidden bg-muted">
        {row.featuredCluster?.hasImage ? (
          <Image
            src={imageApiUrl("cluster", row.featuredCluster.id)}
            alt={row.name}
            fill
            sizes="(max-width: 1024px) 100vw, 420px"
            className="object-cover transition-transform duration-500 group-hover:scale-[1.04]"

          />
        ) : (
          <div className="aurora-fallback absolute inset-0" aria-hidden="true" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/70 via-transparent to-transparent" />
        <div className="absolute left-3 top-3 flex items-center gap-2 rounded-full border border-white/30 bg-black/50 py-1 pl-1 pr-3 backdrop-blur-md">
          <span className="relative inline-block size-7 overflow-hidden rounded-full bg-zinc-700">
            {row.imageUrl ? (
              <Image src={row.imageUrl} alt={row.name} fill sizes="28px" className="object-cover" />
            ) : null}
          </span>
          <span className="text-xs font-bold text-white">{row.name}</span>
        </div>
      </div>
      <div className="flex flex-col gap-2 px-4 pb-4">
        <p className="text-[10px] font-mono uppercase tracking-[0.16em] text-rose-600 dark:text-rose-300">
          {row.recentArticleCount} {copy.ui.inNewsRecently}
        </p>
        {translation ? (
          <p className="text-sm font-bold leading-snug text-foreground line-clamp-2 group-hover:text-rose-600 dark:group-hover:text-rose-300">
            {translation.title}
          </p>
        ) : null}
      </div>
    </Link>
  );
}
